/* Wildfire Smoke Hub — client hydration.
 *
 * Liquid has already painted real numbers from the `custom.smoke_status` metafield, so this
 * file has exactly two jobs:
 *   1. refresh those numbers, because a page served from cache can be up to 15 minutes
 *      behind the metafield and the metafield itself can be behind the APIs;
 *   2. run the ZIP / geolocation lookup, which is per-visitor and can't be pre-rendered.
 *
 * It deliberately does NOT rebuild the page. If any fetch fails the server-rendered values
 * stay exactly as they were, which is the correct failure mode for an air-quality page:
 * slightly stale beats blank or zero.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-smoke-hub]');
  if (!root) return;

  var NWS = 'https://api.weather.gov/alerts/active?event=' + encodeURIComponent('Air Quality Alert');
  var OM = 'https://air-quality-api.open-meteo.com/v1/air-quality';

  var CAT = {
    // slug, pill background, pill text, map dot
    'Good':             ['good',      '#e6f7ea', '#1a7f56', '#a1e3cb'],
    'Moderate':         ['moderate',  '#fff5d6', '#8a6d1f', '#ffe999'],
    'Sensitive groups': ['usg',       '#ffe9d1', '#a2541c', '#ffcb83'],
    'Unhealthy':        ['unhealthy', '#ffe0e0', '#c62828', '#ff4747'],
    'Very unhealthy':   ['very',      '#ece9fb', '#5b4bb8', '#c6a2f8'],
    'Hazardous':        ['hazardous', '#f5e3ec', '#8a2f4d', '#e08bb0']
  };
  function catFor(a) {
    return a <= 50 ? 'Good' : a <= 100 ? 'Moderate' : a <= 150 ? 'Sensitive groups'
         : a <= 200 ? 'Unhealthy' : a <= 300 ? 'Very unhealthy' : 'Hazardous';
  }

  // The city table is NOT defined here. It lives in snippets/rds_smoke-cities.liquid, which
  // the section inlines above, and the same file is injected into the n8n job at deploy time
  // by scripts/smoke-hub-n8n-deploy.py, and into the hero banner build the same way. There
  // were three hand-maintained copies of this list before, covering 19 states between them,
  // and nothing stopped them drifting apart.
  //
  // Missing or unparseable leaves it empty, which makes refresh() bail and keeps whatever
  // Liquid already painted. That is the same failure mode as the fetches below.
  var CITIES = (function () {
    var el = document.querySelector('[data-sh-cities-index]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();

  var alertedStates = {};
  // Whether we actually know the alert picture yet. Seeded from the metafield the section
  // rendered, then replaced by the live NWS fetch. Until one of those lands, "no alert for
  // your state" is a claim we cannot make.
  var alertsKnown = false;
  (root.getAttribute('data-sh-states') || '').split(',').forEach(function (code) {
    if (code) { alertedStates[code] = 1; alertsKnown = true; }
  });

  function setAll(key, val) {
    root.querySelectorAll('[data-sh="' + key + '"]').forEach(function (el) { el.textContent = val; });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function refresh() {
    fetch(NWS).then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        var alerts = j.features || [];
        var byState = {};
        alerts.forEach(function (f) {
          var ugc = (f.properties && f.properties.geocode && f.properties.geocode.UGC) || [];
          var seen = {};
          ugc.forEach(function (u) { seen[u.slice(0, 2)] = 1; });
          Object.keys(seen).forEach(function (st) { byState[st] = (byState[st] || 0) + 1; });
        });
        alertedStates = byState;
        alertsKnown = true;

        setAll('alert_count', alerts.length);
        setAll('state_count', Object.keys(byState).length);

        // Alerts can clear while a cached page is still showing them. Setting the counts to
        // zero and stopping here used to leave the map drawn and the city table populated,
        // so the page read "0 active alerts" directly above a list of alerted cities.
        if (!Object.keys(byState).length) return goClear();

        var pts = [];
        Object.keys(byState).forEach(function (st) {
          (CITIES[st] || []).forEach(function (c) { pts.push({ city: c[0], state: st, lat: c[1], lon: c[2] }); });
        });
        if (!pts.length) return;

        return fetch(OM + '?latitude=' + pts.map(function (p) { return p.lat; }).join(',') +
                     '&longitude=' + pts.map(function (p) { return p.lon; }).join(',') +
                     '&current=us_aqi,pm2_5&timezone=auto')
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function (aq) {
            if (!Array.isArray(aq)) aq = [aq];
            var rows = [];
            pts.forEach(function (p, i) {
              var cur = (aq[i] || {}).current || {};
              if (cur.us_aqi == null) return;
              rows.push({ city: p.city, state: p.state, lat: p.lat, lon: p.lon,
                          aqi: Math.round(cur.us_aqi), pm: Number(cur.pm2_5).toFixed(1) });
            });
            if (!rows.length) return;
            rows.sort(function (a, b) { return b.aqi - a.aqi; });
            paint(rows);
          });
      })
      .catch(function () { /* keep whatever Liquid rendered */ });
  }

  // Take the page down to its all-clear state without a reload. Cannot build the sections
  // the server omits, so the reverse transition - clear page, alert arrives - waits for the
  // next render. That direction is the safe one to be slow about.
  function goClear() {
    var tbody = root.querySelector('[data-sh-cities]');
    if (tbody) {
      tbody.innerHTML = '<tr><td colspan="4" class="rds_sh-muted">' +
        'No active air quality alerts anywhere in the US right now.</td></tr>';
    }
    ['#map', '#alerts'].forEach(function (sel) {
      var sec = root.querySelector(sel);
      if (sec) sec.hidden = true;
    });
    var ticker = root.querySelector('.rds_sh-ticker');
    if (ticker) ticker.hidden = true;
    root.querySelectorAll('[data-sh-ticker]').forEach(function (el) { el.textContent = ''; });
  }

  function paint(rows) {
    var worst = rows[0];
    setAll('worst_aqi', worst.aqi);
    setAll('worst_city', worst.city + ', ' + worst.state);
    setAll('worst_category', catFor(worst.aqi).toLowerCase());

    var pct = Math.max(4, Math.min(96, worst.aqi / 300 * 100)).toFixed(1) + '%';
    var pin = root.querySelector('[data-sh-pin]');
    var lab = root.querySelector('[data-sh-pin-label]');
    if (pin) pin.style.left = pct;
    if (lab) { lab.style.left = pct; lab.textContent = worst.city; }

    var tbody = root.querySelector('[data-sh-cities]');
    if (tbody) {
      // Same rule as the Liquid: a clean-air city inside an alerted state is not "affected".
      var affected = rows.filter(function (r) { return r.aqi > 50; });
      if (!affected.length) {
        tbody.innerHTML = '<tr><td colspan="4" class="rds_sh-muted">No city we track is showing degraded air right now.</td></tr>';
        return;
      }
      tbody.innerHTML = affected.slice(0, 20).map(function (r) {
        var c = catFor(r.aqi);
        return '<tr><td>' + esc(r.city) + ', ' + r.state + '</td>' +
               '<td class="rds_sh-num">' + r.aqi + '</td>' +
               '<td class="rds_sh-muted">' + r.pm + '</td>' +
               '<td><span class="rds_sh-pill rds_sh-pill--' + CAT[c][0] + '">' + c + '</span></td></tr>';
      }).join('');
    }

    // Highlight the band the worst reading currently sits in.
    var bands = root.querySelector('[data-sh-bands]');
    if (bands) {
      var slug = CAT[catFor(worst.aqi)][0];
      bands.querySelectorAll('tr').forEach(function (tr) {
        tr.classList.toggle('is-current', tr.getAttribute('data-band') === slug);
      });
    }

    drawMap(rows);
    var mapT;
    window.addEventListener('resize', function () {
      clearTimeout(mapT);
      mapT = setTimeout(function () { drawMap(rows); }, 250);
    });

    root.querySelectorAll('[data-sh-updated]').forEach(function (el) {
      var d = new Date();
      // en-US explicitly, not the browser locale: `undefined` renders "11 sie, 9:06" on a
      // Polish machine. This is a US storefront, so the stamp is fixed to US format.
      el.textContent = d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      el.setAttribute('datetime', d.toISOString());
    });
  }


  /* -- SMOKE MAP ------------------------------------------------------------
   * The design's map is a React component using d3 + topojson + a remote world-atlas
   * fetch: roughly 280 KB of library plus a third-party request, for one dark map with
   * dots on it, on a page we are asking to be fast.
   *
   * This draws the same picture from an inlined Douglas-Peucker-simplified US outline
   * (353 points, ~5 KB) and about twenty lines of Web Mercator. No dependencies, no extra
   * request, and it cannot break because a CDN moved a file.
   */
var US_OUTLINE = [[[-124.73,48.17],[-122.76,48.14],[-123.15,47.36],[-122.53,47.91],[-122.68,47.1],[-122.23,47.97],[-122.76,49.0],[-95.15,49.0],[-94.96,49.37],[-94.64,48.74],[-93.79,48.52],[-89.49,48.01],[-90.87,47.56],[-91.94,46.68],[-90.87,46.96],[-90.95,46.6],[-90.44,46.56],[-87.8,47.47],[-88.5,46.76],[-88.14,46.97],[-86.82,46.44],[-84.95,46.77],[-85.03,46.48],[-84.13,46.53],[-84.25,46.18],[-83.47,45.98],[-87.05,45.82],[-88.04,44.57],[-86.98,45.29],[-87.91,43.24],[-87.33,41.62],[-86.26,42.44],[-86.54,43.66],[-86.26,44.69],[-85.54,45.21],[-85.53,44.75],[-84.92,45.39],[-85.01,45.76],[-83.38,45.27],[-83.33,44.34],[-83.96,43.75],[-82.64,43.85],[-82.52,42.61],[-83.45,41.71],[-82.48,41.38],[-79.15,42.55],[-78.85,42.78],[-79.07,43.26],[-76.24,43.53],[-76.31,44.2],[-74.97,44.98],[-70.86,45.23],[-69.23,47.46],[-67.79,47.07],[-67.8,45.68],[-66.98,44.81],[-68.19,44.24],[-68.48,44.43],[-68.6,44.01],[-69.0,44.43],[-69.08,43.97],[-70.55,43.32],[-71.01,42.28],[-70.41,41.74],[-70.03,42.02],[-69.99,41.54],[-71.34,41.45],[-71.39,41.78],[-71.48,41.37],[-73.64,41.0],[-71.86,41.07],[-74.06,40.68],[-74.1,39.76],[-74.86,38.94],[-75.53,39.69],[-75.05,38.41],[-75.94,37.09],[-75.66,37.96],[-76.38,38.85],[-75.97,39.56],[-76.58,39.26],[-76.32,38.04],[-77.25,38.64],[-77.27,38.33],[-76.24,37.89],[-76.36,37.61],[-76.91,37.97],[-76.3,37.56],[-76.3,37.0],[-76.95,37.23],[-75.99,36.92],[-75.53,35.79],[-75.99,36.53],[-75.8,36.07],[-76.69,36.28],[-76.67,35.94],[-76.06,35.99],[-76.04,35.67],[-75.81,35.96],[-75.73,35.63],[-76.14,35.33],[-77.02,35.52],[-76.47,35.26],[-76.98,35.0],[-76.29,35.01],[-76.52,34.65],[-76.04,35.06],[-77.56,34.42],[-77.96,33.85],[-78.81,33.74],[-81.2,31.72],[-81.45,30.5],[-80.03,26.8],[-80.37,25.29],[-80.18,25.49],[-80.66,24.9],[-80.44,25.19],[-81.14,25.16],[-82.07,26.96],[-82.27,26.72],[-82.74,27.54],[-82.41,27.9],[-82.69,28.03],[-82.73,27.61],[-82.85,27.86],[-82.69,28.91],[-83.64,29.89],[-84.27,30.1],[-85.05,29.59],[-86.3,30.36],[-88.0,30.23],[-88.01,30.68],[-88.14,30.32],[-89.31,30.38],[-89.86,30.0],[-89.22,29.97],[-89.68,29.62],[-89.0,29.18],[-89.42,28.93],[-89.28,29.18],[-89.84,29.32],[-90.87,29.06],[-91.83,29.83],[-94.73,29.37],[-94.49,29.52],[-94.94,29.7],[-95.17,29.11],[-94.73,29.33],[-97.21,27.6],[-97.16,26.08],[-97.36,27.35],[-96.42,28.4],[-97.52,27.85],[-97.25,27.7],[-97.64,27.27],[-97.15,26.06],[-97.37,25.84],[-99.08,26.4],[-99.51,27.56],[-101.41,29.76],[-102.67,29.74],[-103.28,28.98],[-104.51,29.64],[-104.92,30.6],[-106.45,31.76],[-108.21,31.78],[-108.21,31.33],[-111.08,31.33],[-114.72,32.72],[-117.12,32.53],[-117.5,33.33],[-118.54,34.04],[-120.62,34.55],[-120.63,35.12],[-122.4,37.2],[-122.52,37.78],[-122.05,37.46],[-122.3,38.11],[-122.53,37.82],[-123.01,38.0],[-124.36,40.26],[-124.06,41.44],[-124.55,42.84],[-124.0,46.24],[-123.55,46.27],[-124.08,46.27],[-123.86,46.95],[-124.18,46.93],[-124.73,48.17]],[[-168.13,65.66],[-164.4,66.58],[-163.61,66.56],[-164.09,66.18],[-163.77,66.06],[-160.99,66.23],[-162.63,66.86],[-161.57,66.44],[-161.29,66.52],[-161.88,66.72],[-161.48,66.96],[-163.67,67.1],[-164.21,67.64],[-166.84,68.34],[-166.3,68.46],[-166.22,68.88],[-163.53,69.14],[-161.88,70.33],[-156.57,71.35],[-155.57,71.17],[-155.94,70.84],[-155.03,71.15],[-154.17,70.77],[-152.22,70.82],[-152.42,70.61],[-151.88,70.43],[-143.25,70.15],[-141.0,69.65],[-141.0,60.31],[-139.09,60.36],[-139.2,60.09],[-137.53,58.91],[-136.58,59.17],[-136.35,59.6],[-135.25,59.7],[-133.38,58.43],[-131.84,56.6],[-130.1,56.12],[-129.98,55.28],[-130.7,54.72],[-131.22,55.98],[-130.93,55.58],[-131.16,55.2],[-131.84,55.46],[-131.69,55.89],[-132.15,55.56],[-131.94,56.19],[-132.61,56.01],[-132.21,56.46],[-133.68,57.62],[-133.16,57.6],[-134.05,58.06],[-134.01,58.4],[-134.63,58.25],[-135.53,59.32],[-135.06,58.19],[-135.91,58.38],[-136.16,58.98],[-136.92,58.95],[-136.04,58.38],[-136.7,58.22],[-139.86,59.54],[-139.54,60.04],[-140.31,59.69],[-141.55,60.17],[-144.59,59.8],[-144.05,60.04],[-145.76,60.59],[-146.65,60.24],[-145.8,60.59],[-146.8,60.81],[-146.29,61.12],[-148.14,60.79],[-147.74,61.27],[-148.45,60.79],[-148.11,60.74],[-148.33,60.48],[-147.94,60.44],[-148.31,60.25],[-147.92,59.97],[-149.38,59.84],[-149.42,60.12],[-149.53,59.71],[-149.74,59.95],[-150.39,59.34],[-150.32,59.61],[-151.98,59.25],[-150.98,59.78],[-151.85,59.74],[-151.41,60.72],[-150.38,61.04],[-149.0,60.83],[-150.07,61.16],[-149.61,61.49],[-151.79,60.86],[-152.75,60.23],[-152.71,59.92],[-154.12,59.37],[-154.16,59.02],[-153.25,58.86],[-154.21,58.14],[-158.4,56.46],[-158.12,56.23],[-158.44,55.99],[-161.23,55.36],[-161.53,55.63],[-162.05,55.07],[-163.19,55.09],[-163.37,54.8],[-163.06,54.66],[-164.84,54.42],[-164.49,54.92],[-163.32,54.88],[-161.81,55.89],[-160.29,55.77],[-160.59,55.98],[-160.36,56.28],[-157.59,57.49],[-157.53,58.41],[-156.93,58.97],[-158.14,58.62],[-158.53,59.0],[-158.9,58.39],[-160.32,59.07],[-161.75,58.55],[-162.17,58.65],[-161.77,58.78],[-162.06,59.27],[-161.7,59.49],[-162.37,60.17],[-163.93,59.8],[-165.36,60.51],[-164.95,61.07],[-165.59,61.11],[-166.09,61.82],[-165.64,61.85],[-164.42,63.21],[-163.05,63.06],[-162.3,63.54],[-161.14,63.5],[-160.77,63.83],[-161.51,64.42],[-160.78,64.72],[-161.19,64.92],[-162.79,64.33],[-163.17,64.64],[-166.24,64.58],[-166.9,65.14],[-166.35,65.28],[-168.13,65.66]],[[-133.82,55.96],[-133.16,56.32],[-132.14,55.46],[-132.61,55.49],[-131.99,55.26],[-131.96,54.79],[-133.12,55.25],[-132.63,54.88],[-132.87,54.7],[-133.47,55.25],[-133.18,55.59],[-133.7,55.78],[-133.32,55.82],[-133.82,55.96]]];

  function mercator(lon, lat) {
    var s = Math.sin(lat * Math.PI / 180);
    return [(lon + 180) / 360, 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)];
  }

  function drawMap(rows) {
    var box = root.querySelector('[data-sh-map]');
    if (!box || !rows.length) return;
    var svg = box.querySelector('svg');
    var tip = box.querySelector('[data-sh-map-tip]');
    var W = box.clientWidth, H = box.clientHeight;
    if (W < 40 || H < 40) return;

    // Frame on the readings, not a fixed western box: in July the smoke sat over the Great
    // Lakes, and a hardcoded frame would have rendered an empty map.
    var lons = rows.map(function (r) { return r.lon; });
    var lats = rows.map(function (r) { return r.lat; });
    var pad = 7;
    var b0 = mercator(Math.min.apply(null, lons) - pad, Math.max.apply(null, lats) + pad);
    var b1 = mercator(Math.max.apply(null, lons) + pad, Math.min.apply(null, lats) - pad);
    var k = Math.max(W / (b1[0] - b0[0]), H / (b1[1] - b0[1]));   // cover, like the design's slice fit
    var ox = (W - (b1[0] - b0[0]) * k) / 2, oy = (H - (b1[1] - b0[1]) * k) / 2;
    function proj(lon, lat) {
      var m = mercator(lon, lat);
      return [(m[0] - b0[0]) * k + ox, (m[1] - b0[1]) * k + oy];
    }

    var NS = 'http://www.w3.org/2000/svg';
    function el(n, a) {
      var e = document.createElementNS(NS, n);
      for (var key in a) e.setAttribute(key, a[key]);
      return e;
    }
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);

    var defs = el('defs');
    var f = el('filter', { id: 'sh-glow', x: '-80%', y: '-80%', width: '260%', height: '260%' });
    f.appendChild(el('feGaussianBlur', { stdDeviation: 8, result: 'b' }));
    var mg = el('feMerge');
    mg.appendChild(el('feMergeNode', { 'in': 'b' }));
    mg.appendChild(el('feMergeNode', { 'in': 'SourceGraphic' }));
    f.appendChild(mg); defs.appendChild(f); svg.appendChild(defs);

    svg.appendChild(el('rect', { width: W, height: H, fill: '#111' }));

    var d = '';
    US_OUTLINE.forEach(function (ring) {
      ring.forEach(function (pt, i) {
        var q = proj(pt[0], pt[1]);
        d += (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1);
      });
      d += 'Z';
    });
    svg.appendChild(el('path', { d: d, fill: '#262626',
      stroke: 'rgba(255,255,255,.3)', 'stroke-width': 1.2 }));

    var plume = el('g', { opacity: 0.5, filter: 'url(#sh-glow)' });
    rows.filter(function (r) { return r.aqi > 80; }).forEach(function (r) {
      var q = proj(r.lon, r.lat);
      plume.appendChild(el('circle', { cx: q[0], cy: q[1], r: 30 + r.aqi / 3.5,
        fill: CAT[catFor(r.aqi)][3], opacity: 0.14 }));
    });
    svg.appendChild(plume);

    // Labels go worst-first and any that would collide is dropped, which is why some
    // low-AQI cities show a dot with no name.
    var boxes = [];
    rows.forEach(function (r) {
      var q = proj(r.lon, r.lat), rad = 4 + (r.aqi / 100) * 4;
      boxes.push({ x1: q[0] - rad - 3, x2: q[0] + rad + 3, y1: q[1] - rad - 3, y2: q[1] + rad + 3 });
    });
    function fits(b) {
      return !boxes.some(function (o) {
        return !(b.x2 < o.x1 || b.x1 > o.x2 || b.y2 < o.y1 || b.y1 > o.y2);
      });
    }

    var g = el('g');
    rows.slice().sort(function (a, b) { return b.aqi - a.aqi; }).forEach(function (r) {
      var q = proj(r.lon, r.lat);
      var color = CAT[catFor(r.aqi)][3];
      var rad = 4 + (r.aqi / 100) * 4;
      var node = el('g');
      node.appendChild(el('circle', { cx: q[0], cy: q[1], r: rad + 9, fill: color, opacity: 0.12 }));
      node.appendChild(el('circle', { cx: q[0], cy: q[1], r: rad, fill: color,
        stroke: '#111', 'stroke-width': 1.5 }));
      node.addEventListener('mouseenter', function () {
        if (!tip) return;
        tip.style.display = 'block';
        tip.style.left = Math.min(q[0] + 14, W - 170) + 'px';
        tip.style.top = Math.max(8, q[1] - 14) + 'px';
        tip.innerHTML = '<b>' + esc(r.city) + ', ' + r.state + '</b>' +
          '<span style="color:' + color + '">AQI ' + r.aqi + ' &middot; ' + catFor(r.aqi) + '</span>' +
          '<span class="rds_sh-maptip__pm">PM2.5 ' + r.pm + ' &micro;g/m&sup3;</span>';
      });
      node.addEventListener('mouseleave', function () { if (tip) tip.style.display = 'none'; });

      var text = r.city + ' ' + r.aqi, tw = text.length * 6.2;
      var cands = [
        { x: q[0] + rad + 8, y: q[1] + 4, a: 'start' },
        { x: q[0] - rad - 8, y: q[1] + 4, a: 'end' },
        { x: q[0], y: q[1] - rad - 9, a: 'middle' },
        { x: q[0], y: q[1] + rad + 16, a: 'middle' }
      ];
      for (var i = 0; i < cands.length; i++) {
        var c = cands[i];
        var x1 = c.a === 'start' ? c.x : c.a === 'end' ? c.x - tw : c.x - tw / 2;
        var bb = { x1: x1, x2: x1 + tw, y1: c.y - 11, y2: c.y + 3 };
        if (bb.x1 > 2 && bb.x2 < W - 2 && bb.y1 > 2 && bb.y2 < H - 2 && fits(bb)) {
          boxes.push({ x1: x1 - 3, x2: x1 + tw + 3, y1: c.y - 12, y2: c.y + 4 });
          var t = el('text', { x: c.x, y: c.y, 'text-anchor': c.a, 'font-size': 11.5,
            'font-family': 'Poppins, system-ui, sans-serif', 'paint-order': 'stroke',
            stroke: '#111', 'stroke-width': 3, 'stroke-linejoin': 'round',
            fill: r.aqi > 80 ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.55)' });
          t.textContent = text;
          node.appendChild(t);
          break;
        }
      }
      g.appendChild(node);
    });
    svg.appendChild(g);

    var load = box.querySelector('[data-sh-map-loading]');
    if (load) load.style.display = 'none';
  }

  /* ── ZIP / geolocation ─────────────────────────────────────────────── */
  var out = root.querySelector('[data-sh-area-result]');

  function msg(t) { if (out) out.innerHTML = '<p class="rds_sh-muted">' + esc(t) + '</p>'; }

  function show(label, aqi, pm, state) {
    if (!out) return;
    var c = catFor(aqi);
    out.innerHTML =
      '<div class="rds_sh-areacard">' +
        '<div class="rds_sh-areacard__l"><span class="rds_sh-areacard__n">' + aqi + '</span>' +
          '<span><span class="rds_sh-areacard__c">' + esc(label) + '</span>' +
          '<span class="rds_sh-muted">PM2.5 ' + pm + ' µg/m³</span></span></div>' +
        '<div class="rds_sh-areacard__r">' +
          '<span class="rds_sh-pill rds_sh-pill--' + CAT[c][0] + '">' + c + '</span>' +
          '<span class="rds_sh-muted">' + alertLine(state) + '</span>' +
        '</div></div>';
  }

  function alertLine(state) {
    if (state && alertedStates[state]) return 'Your state is under an air quality alert';
    if (state && alertsKnown) return 'No active alert for your state';
    return 'Alert status for your state is unavailable';
  }

  function aqiAt(lat, lon) {
    return fetch(OM + '?latitude=' + lat + '&longitude=' + lon + '&current=us_aqi,pm2_5&timezone=auto')
      .then(function (r) { return r.json(); })
      .then(function (j) { return { aqi: Math.round(j.current.us_aqi), pm: Number(j.current.pm2_5).toFixed(1) }; });
  }

  var note = root.querySelector('[data-sh-area-note]');

  function setNote(text) {
    if (!note) return;
    if (text) { note.textContent = text; note.hidden = false; }
    else { note.textContent = ''; note.hidden = true; }
  }

  function check(detectedFrom) {
    var input = root.querySelector('#rds_sh-zip');
    var zip = (input && input.value || '').trim();
    if (!/^\d{5}$/.test(zip)) return msg('Enter a 5-digit US ZIP code.');
    msg('Checking…');
    // Zippopotam is free, keyless and CORS-open. Nothing else on the page depends on it, so
    // if it disappears only this lookup degrades.
    fetch('https://api.zippopotam.us/us/' + zip)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        var p = j.places[0];
        return aqiAt(Number(p.latitude), Number(p.longitude)).then(function (a) {
          show(p['place name'] + ', ' + p['state abbreviation'], a.aqi, a.pm, p['state abbreviation']);
          setNote(detectedFrom ? 'Showing ' + zip + ', ' + detectedFrom + '.' : '');
        });
      })
      .catch(function () {
        setNote('');
        msg('No match for that ZIP, or the lookup is unavailable.');
      });
  }

  // Open-Meteo returns air quality but no political geography, so a browser-geolocation
  // reading used to come back with state = null and the card could never say whether the
  // visitor was in an alerted state — the one thing this section exists to answer. NWS's
  // points endpoint resolves lat/lon to a city and state, is the same source the alerts
  // come from, and is keyless and CORS-open like everything else here.
  function placeAt(lat, lon) {
    return fetch('https://api.weather.gov/points/' + lat.toFixed(4) + ',' + lon.toFixed(4))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        var p = j.properties && j.properties.relativeLocation && j.properties.relativeLocation.properties;
        return p && p.state ? { city: p.city, state: p.state } : null;
      })
      .catch(function () { return null; });
  }

  function locate() {
    if (!navigator.geolocation) return msg('Location is not available in this browser.');
    msg('Getting your location…');
    navigator.geolocation.getCurrentPosition(function (pos) {
      var lat = pos.coords.latitude, lon = pos.coords.longitude;
      Promise.all([aqiAt(lat, lon), placeAt(lat, lon)])
        .then(function (res) {
          var a = res[0], p = res[1];
          show(p ? p.city + ', ' + p.state : 'Your location', a.aqi, a.pm, p && p.state);
          setNote('');
        })
        .catch(function () { msg('Could not read air quality for your location.'); });
    }, function () { msg('Location permission denied.'); });
  }

  /* ── visitor geo already on hand ───────────────────────────────────── */
  // The header resolves every US/CA visitor to a city + ZIP through ipinfo and caches it in
  // sessionStorage.geo (assets/main.js), and the deliver-to strip and delivery estimates
  // both read it. This section was the one feature that ignored all of that and made the
  // visitor type a ZIP to find out whether their own air was safe. Reuse what we have and
  // answer the question before they ask it; the form stays for checking anywhere else.
  function storedZip() {
    var raw;
    try { raw = sessionStorage.getItem('geo'); } catch (e) { return null; }
    if (!raw) return null;
    try {
      var g = JSON.parse(raw);
      // US only. The alert data is NWS, so a Canadian ZIP would resolve to nothing useful.
      if (!g || g.countryCode !== 'US') return null;
      return /^\d{5}$/.test(String(g.zipCode || '')) ? String(g.zipCode) : null;
    } catch (e) { return null; }
  }

  var zin = root.querySelector('#rds_sh-zip');
  var autoRan = false;

  function autoCheck() {
    if (autoRan) return true;
    var zip = storedZip();
    if (!zip) return false;
    autoRan = true;
    // Never overwrite something the visitor is in the middle of typing.
    if (zin && !zin.value.trim()) zin.value = zip;
    // Deliberately vague about the source. sessionStorage.geo starts out IP-derived but the
    // header's location modal writes the visitor's own ZIP to the same key, so by the time
    // this reads it there is no way to tell which one it is. "Saved for this session" is
    // true either way; "detected from your connection" would be a guess.
    check('the location saved for this session. It can be approximate, so enter a ZIP code ' +
          'to check a specific area');
    return true;
  }

  // The geo bootstrap is an async fetch that may not have landed when this file runs, so
  // poll briefly for it. Same shape as module_delivery.js, which waits on the same key.
  if (!autoCheck()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (autoCheck() || ++tries >= 12) clearInterval(poll);
    }, 500);
  }

  // Changing the ZIP in the header's location modal is an explicit statement about where
  // the visitor is, so it wins over both the detected ZIP and whatever is in this input.
  window.addEventListener('wb:geo-updated', function () {
    var zip = storedZip();
    if (!zip) return;
    autoRan = false;
    if (zin) zin.value = zip;
    autoCheck();
  });

  var cbtn = root.querySelector('[data-sh-check]');
  var lbtn = root.querySelector('[data-sh-locate]');
  if (cbtn) cbtn.addEventListener('click', function () { autoRan = true; check(); });
  if (lbtn) lbtn.addEventListener('click', function () { autoRan = true; locate(); });
  if (zin) zin.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); autoRan = true; check(); }
  });

  refresh();
})();
